import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(desc: string, lineSpacing: number, bScale: number) {
  if (!desc) return null;
  const lines = desc.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const style = { fontSize: `${0.875 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="text-slate-600 whitespace-pre-line" style={style}>{desc}</p>;
  }
  return (
    <ul className="list-disc pl-4 space-y-1 text-slate-600" style={style}>
      {lines.map((line, i) => <li key={i}>{line.replace(/^[-•\s]+/, '')}</li>)}
    </ul>
  );
}

export function ClassicTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;

  return (
    <div
      className="bg-white text-black h-full"
      style={{
        fontFamily: `${s.fonts.primary}, Georgia, 'Times New Roman', serif`,
        padding: pagePadding(s.margins),
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="text-center border-b-2 border-black pb-6 pt-10" style={{ textAlign: s.headerAlignment }}>
        <h1 className="font-bold tracking-wide uppercase" style={{ fontFamily: `${s.fonts.secondary}, Georgia, serif`, fontSize: `${2.25 * hScale}rem` }}>
          {personal.fullName || 'Your Name'}
        </h1>
        {s.photo && (
          <img src={s.photo} alt="" className="w-20 h-20 rounded-full object-cover mx-auto mt-3 border-2 border-black" />
        )}
        <div className="mt-3 flex flex-wrap justify-center gap-4 text-sm">
          {personal.email && <span>{personal.email}</span>}
          {personal.phone && <span>• {personal.phone}</span>}
          {personal.location && <span>• {personal.location}</span>}
          {personal.website && <span>• {personal.website}</span>}
          {personal.linkedin && <span>• {personal.linkedin}</span>}
        </div>
      </header>

      <main style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.sectionGap }}>
        {personal.summary && (
          <section>
            <p className="whitespace-pre-line" style={{ fontSize: `${0.95 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
          </section>
        )}

        {experience.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wider border-b border-black pb-1 mb-3" style={{ fontSize: `${0.875 * hScale}rem` }}>WORK EXPERIENCE</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {experience.map((exp) => (
                <div key={exp.id}>
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-bold" style={{ fontSize: `${1 * hScale}rem` }}>{exp.company}</h3>
                    <span className="italic" style={{ fontSize: `${0.75 * bScale}rem` }}>
                      {formatDate(exp.startDate, s.dateFormat)} — {exp.current ? 'Present' : formatDate(exp.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="italic" style={{ fontSize: `${0.875 * bScale}rem` }}>{exp.title}{exp.location && `, ${exp.location}`}</p>
                  {renderDescription(exp.description, s.spacing.lineSpacing, bScale)}
                </div>
              ))}
            </div>
          </section>
        )}

        {education.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wider border-b border-black pb-1 mb-3" style={{ fontSize: `${0.875 * hScale}rem` }}>Education</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {education.map((edu) => (
                <div key={edu.id}>
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-bold" style={{ fontSize: `${1 * hScale}rem` }}>{edu.school}</h3>
                    <span className="italic" style={{ fontSize: `${0.75 * bScale}rem` }}>
                      {formatDate(edu.startDate, s.dateFormat)} — {formatDate(edu.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="italic" style={{ fontSize: `${0.875 * bScale}rem` }}>{edu.degree}{edu.field && `, ${edu.field}`}</p>
                  {edu.description && <p className="mt-0.5" style={{ fontSize: `${0.75 * bScale}rem` }}>{edu.description}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {skills.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wider border-b border-black pb-1 mb-2" style={{ fontSize: `${0.875 * hScale}rem` }}>Skills</h2>
            <p style={{ fontSize: `${0.875 * bScale}rem` }}>{skills.map((sk) => sk.name).join(' • ')}</p>
          </section>
        )}

        {projects.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wider border-b border-black pb-1 mb-3" style={{ fontSize: `${0.875 * hScale}rem` }}>Projects</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {projects.map((proj) => (
                <div key={proj.id}>
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-bold" style={{ fontSize: `${1 * hScale}rem` }}>{proj.name}</h3>
                    {proj.url && <a href={proj.url} className="italic underline" style={{ fontSize: `${0.75 * bScale}rem` }}>{proj.url}</a>}
                  </div>
                  <p className="mt-0.5 text-justify" style={{ fontSize: `${0.875 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{proj.description}</p>
                  {proj.technologies.length > 0 && <p className="italic mt-1" style={{ fontSize: `${0.75 * bScale}rem` }}>Technologies: {proj.technologies.join(', ')}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {certifications.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wider border-b border-black pb-1 mb-2" style={{ fontSize: `${0.875 * hScale}rem` }}>Certifications</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {certifications.map((cert) => (
                <div key={cert.id} className="flex justify-between items-baseline">
                  <span className="font-medium" style={{ fontSize: `${0.875 * bScale}rem` }}>{cert.name}</span>
                  <span className="italic" style={{ fontSize: `${0.75 * bScale}rem` }}>{cert.issuer}, {cert.date}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
